import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useLanguage } from '@cobliteam/i18n';
import KnockProviders from '@cobliteam/notifications/src/providers/KnockProviders';
import { Environment } from '@cobliteam/react-environments';
import type { ReactNode } from 'react';

/**
 * Gate de sessão/flag/ambiente dos providers in-app do Knock.
 *
 * Lê sessão, idioma, feature flag e config de ambiente, e — quando tudo está disponível — monta a
 * composição de providers do Knock (`KnockProviders`, em `@cobliteam/notifications`) uma única vez
 * no topo da árvore privada (acima do layout switcher), para que o sino do feed in-app do header
 * (`KnockFeedButton`) e o modal global de guide compartilhem um único client + websocket do Knock.
 *
 * Protegido pela feature flag `knock-in-app` (`knockInApp` no client). A config vem de
 * `@cobliteam/react-environments` (`knockAPIKey`, `knockFeedChannelId`, `knockGuideChannelId`), que
 * leem as variáveis de ambiente `REACT_APP_KNOCK_*` com defaults de prod. Session+user são
 * garantidos pelo PrivateRouterProvider, mas aborta se o Knock estiver off/não configurado.
 */
const KnockInAppProvider = ({ children }: { children: ReactNode }) => {
  const { getSession } = useUser();
  const session = getSession();
  const userId = session?.user?.id;
  const fleetId = session?.fleet?.id;
  const { knockInApp } = useFeatureFlags();
  const { currentLanguage } = useLanguage();

  const { knockAPIKey, knockFeedChannelId, knockGuideChannelId } = Environment.getInstance().getEnvironment();

  // `fleetId` no gate: o feed/guide do Knock recebem `tenant: fleetId`. Sem fleet, `tenant` undefined
  // deixaria o feed sem escopo (risco de vazar notificações de outra frota) — melhor não montar o Knock.
  if (!(knockInApp && knockAPIKey && userId && fleetId)) return <>{children}</>;

  return (
    <KnockProviders
      apiKey={knockAPIKey}
      userId={String(userId)}
      fleetId={fleetId}
      language={currentLanguage}
      feedChannelId={knockFeedChannelId}
      guideChannelId={knockGuideChannelId}
    >
      {children}
    </KnockProviders>
  );
};

export default KnockInAppProvider;
